import React, { useState, useEffect, useCallback } from 'react';
import {
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid';
import { useParams, Link } from 'react-router-dom';
import storeApi from '../../../services/storeApi';
import {
  BuildingStorefrontIcon,
  Cog8ToothIcon,
  TagIcon,
  ClockIcon,
  BriefcaseIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { useSpringRef, useTransition, animated } from '@react-spring/web';
import StoreTab from './tabs/store-tab';
import OrderTab from './tabs/order-tab';
import BrandingTab from './tabs/branding-tab';
import TimezoneTab from './tabs/timezone-tab';
import DangerZoneTab from './tabs/danger-zone-tab';
import TaxesTab from './tabs/taxes-tab';
import WorkingHoursTab from './tabs/working-hours-tab';
import StoreSlugUpsertModal from './components/store-slug-upsert-modal';
import Notification from '../../../components/notification/notification';
import NoStoreLogo from '../../../assets/no-store-logo.svg';
import StoreSettingsSkeleton from './components/store-settings-skeleton';
import { PageHeader } from '../../../components/ui';
import './store-settings.css';

const tabs = [
  { name: 'Store', icon: BuildingStorefrontIcon, id: 0 },
  { name: 'Order Settings', icon: Cog8ToothIcon, id: 1 },
  { name: 'Branding', icon: TagIcon, id: 2 },
  { name: 'Taxes', icon: ReceiptPercentIcon, id: 3 },
  { name: 'Timezone', icon: ClockIcon, id: 4 },
  { name: 'Working Hours', icon: BriefcaseIcon, id: 5 },
  { name: 'Danger Zone', icon: TrashIcon, id: 6 },
];

const cn = (...classes) => classes.filter(Boolean).join(' ');

const StoreSettings = () => {
  const { id } = useParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [storeDetail, setStoreDetail] = useState({});
  const [dialogs, setDialogs] = useState({ slugUpsert: false });
  const [loading, setLoading] = useState({
    initialLoading: true,
    upsertLoading: false,
    deleteLoading: false,
    loginAsCompany: false,
  });
  const [errors, setErrors] = useState({ slug: false });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [isNext, setIsNext] = useState(false);

  const transRef = useSpringRef();
  const transitions = useTransition(currentTab, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
      transform: isNext ? 'translate3d(10%,0,0)' : 'translate3d(-10%,0,0)',
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: {
      opacity: 0,
      transform: isNext ? 'translate3d(-10%,0,0)' : 'translate3d(10%,0,0)',
    },
  });

  const getStoreDetail = useCallback(
    async (withoutLoader = false) => {
      try {
        setLoading((prev) => ({
          ...prev,
          initialLoading: withoutLoader ? false : true,
        }));
        const res = await storeApi.getById(id);
        if (res.data) {
          setStoreDetail(res.data.data);
        }
      } catch (error) {
        // optional
      } finally {
        setLoading((prev) => ({ ...prev, initialLoading: false }));
      }
    },
    [id],
  );

  const checkUpdate = (val, msg) => {
    if (val) {
      getStoreDetail(true);
      setNotf((prev) => ({
        ...prev,
        message: 'Store Updated Successfully',
        display: true,
        type: 'success',
      }));
    } else {
      setNotf((prev) => ({
        ...prev,
        message: msg || 'Store Update Failed',
        display: true,
        type: 'error',
      }));
    }
  };

  const tabComponents = [
    <StoreTab
      key="store"
      storeDetail={storeDetail}
      checkUpdate={(val, msg) => checkUpdate(val, msg)}
    />,
    <OrderTab
      key="order"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
    <BrandingTab
      key="branding"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
    <TaxesTab
      key="taxes"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
    <TimezoneTab
      key="tz"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
    <WorkingHoursTab
      key="hours"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
    <DangerZoneTab
      key="danger"
      storeDetail={storeDetail}
      checkUpdate={(val) => checkUpdate(val)}
    />,
  ];

  useEffect(() => {
    transRef.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  useEffect(() => {
    getStoreDetail();
  }, [getStoreDetail]);

  const onClickTab = (tab) => {
    setIsNext(tab.id > currentTab);
    setCurrentTab(tab.id);
  };

  const onClickTabMobile = (tabId) => {
    setIsNext(tabId > currentTab);
    setCurrentTab(tabId);
  };

  const onCloseStoreSlugModal = () => {
    setErrors((prev) => ({ ...prev, slug: false }));
    setDialogs((prev) => ({ ...prev, slugUpsert: false }));
  };

  const onClickOpenStoreSlugModal = () => {
    setDialogs((prev) => ({ ...prev, slugUpsert: true }));
  };

  const onSubmitStoreSlugModal = async (slug) => {
    if (slug === '' || !slug) {
      setErrors((prev) => ({ ...prev, slug: true }));
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = {
        ...storeDetail,
        slug,
        websiteclosedat: storeDetail.websiteclosedat || '',
        apartment: storeDetail?.apartment || '',
      };
      const formData = new FormData();
      for (const key of Object.keys(body)) {
        formData.append(key, body[key]);
      }
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(formData, config);
      if (res.data) checkUpdate(true);
    } catch (error) {
      checkUpdate(false, error);
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      onCloseStoreSlugModal();
    }
  };

  const headerActions = storeDetail.slug ? (
    <Link
      to={`${process.env.REACT_APP_ORDER_URL}/${storeDetail.slug}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-md bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-200/90 dark:bg-emerald-900/40 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
    >
      <span className="h-2 w-2 rounded-full bg-emerald-500" />
      {process.env.REACT_APP_ORDER_URL.replace('https://', '')}/
      {storeDetail.slug}
      <ArrowTopRightOnSquareIcon
        className="h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-300"
        aria-hidden
      />
    </Link>
  ) : (
    <button
      type="button"
      onClick={onClickOpenStoreSlugModal}
      className="inline-flex items-center justify-center rounded-md bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-200/90 dark:bg-amber-900/35 dark:text-amber-100 dark:hover:bg-amber-900/50"
    >
      Create order link
    </button>
  );

  return (
    <div className="store-settings-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      {loading.initialLoading ? (
        <StoreSettingsSkeleton />
      ) : (
        <div className="mx-auto max-w-5xl space-y-8">
          <div className="reveal flex items-start gap-4">
            <img
              className="h-14 w-14 shrink-0 rounded-md object-cover ring-2 ring-white shadow-sm dark:ring-slate-700"
              src={storeDetail.storelogo || NoStoreLogo}
              alt=""
            />
            <PageHeader
              title={storeDetail.name}
              description="Branding, ordering, taxes, hours, and more for this location."
              actions={headerActions}
              backTo={`/dashboard/stores/${id}`}
              backLabel="Back to store"
              className="!flex-1"
            />
          </div>

          <div className="reveal space-y-4">
            <nav
              className="flex gap-2 overflow-x-auto rounded-md border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:flex-wrap sm:overflow-visible"
              aria-label="Settings sections"
              style={{ scrollbarWidth: 'none' }}
            >
              {tabs.map((tab) => {
                const isActive = currentTab === tab.id;
                const isDanger = tab.id === 6;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => onClickTab(tab)}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition sm:px-4',
                      isActive && !isDanger
                        ? 'bg-blue-600 text-white shadow-sm dark:bg-sky-500'
                        : isActive && isDanger
                          ? 'bg-rose-600 text-white shadow-sm dark:bg-rose-700'
                          : isDanger
                            ? 'text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40'
                            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <tab.icon className="h-5 w-5 shrink-0" aria-hidden />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="reveal rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6 dark:border-slate-700 dark:bg-slate-800/80">
            {transitions((style, item) => (
              <animated.div className="relative w-full" style={{ ...style }}>
                {tabComponents[item]}
              </animated.div>
            ))}
          </div>
        </div>
      )}

      <StoreSlugUpsertModal
        onSubmit={onSubmitStoreSlugModal}
        show={dialogs.slugUpsert}
        loading={loading.upsertLoading}
        storeDetail={storeDetail}
        setStoreDetail={setStoreDetail}
        onClose={onCloseStoreSlugModal}
        errors={errors}
      />

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default StoreSettings;
